import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { noop } from 'ui-components/default-props/noop';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { useGetPromotionalMigrationPlan } from './use-get-promotional-migration-plan';
import { useGetEmailMarketingHandoffModalDetails } from './use-get-email-marketing-handoff-modal-details';
import { MigratePlanEmailMarketingHandoffModal } from './email-marketing-handoff-modal';
import { MigratePlanModal } from './migration-plan-modal';
import { MigrationPlanGetRewardedModal } from './get-rewarded-modal';
import { useTrackPromoModal } from './hooks/use-track-promo-modal';
import { modalMessages } from './messages';
import { LogService } from '../../../../../services/log-service';
import { useGlobalModalLaunchContext } from '../../../../../contexts/modal-context/global-modal-context';
import { useEnableSelfMigration } from '../../../../../feature-flags/hooks/use-enable-self-migration';

export const ModalsToShow = {
  EmailHandoff: 'EmailHandoff',
  MigratePlan: 'MigratePlan',
  SuccessfullyMigrated: 'SuccessfullyMigrated',
};

export const NewLineupPromotionalMigrationLoader = ({
  closeOfferModal = noop,
  shouldStartWithEmailHandoff,
  hideMarketingHandoffModal: hideMarketingHandoffModalProp,
}) => {
  const { trackPromoCancelEvent, trackPromoCompleteEvent, trackPromoErrorEvent, trackPromoStartEvent } = useTrackPromoModal();
  const [modalToShow, setModalToShow] = useState(shouldStartWithEmailHandoff ? ModalsToShow.EmailHandoff : ModalsToShow.MigratePlan);
  const { hideMarketingHandoffModal: hideMarketingHandoffModalFromHook } = useGetEmailMarketingHandoffModalDetails();
  // Use prop if provided (from global context), otherwise use hook
  const hideMarketingHandoffModal = hideMarketingHandoffModalProp ?? hideMarketingHandoffModalFromHook;
  const { currentPlanDetails, isRequestingPlanChange, onChangePlan, promotionalPlanDetails } = useGetPromotionalMigrationPlan();
  const isSelfMigrationEnabled = useEnableSelfMigration();
  const { openManageSubscriptionDrawer } = useGlobalModalLaunchContext();

  // When self-migration enabled and email handoff flow detected, open drawer instead of modal
  useEffect(() => {
    if (shouldStartWithEmailHandoff && isSelfMigrationEnabled) {
      hideMarketingHandoffModal();
      trackPromoStartEvent('SelfMigrationDrawer');
      openManageSubscriptionDrawer();
    }
  }, [shouldStartWithEmailHandoff, isSelfMigrationEnabled, hideMarketingHandoffModal, trackPromoStartEvent, openManageSubscriptionDrawer]);

  const showMigratePlanModal = () => {
    hideMarketingHandoffModal();
    setModalToShow(ModalsToShow.MigratePlan);
  };

  const showSuccessModal = () => setModalToShow(ModalsToShow.SuccessfullyMigrated);
  const hideAllModals = () => setModalToShow(null);

  const onClose = useCallback(() => {
    if (modalToShow === ModalsToShow.MigratePlan) {
      trackPromoCancelEvent();
    }
    hideAllModals();
    closeOfferModal();
  }, [closeOfferModal, modalToShow, trackPromoCancelEvent]);

  const handleOfferAcceptance = useCallback(async () => {
    try {
      await onChangePlan();
      showSuccessModal();
      trackPromoCompleteEvent();
    } catch (err) {
      trackPromoErrorEvent(err);
      LogService.logError(new Error('Error changing plans via promotional migration flow', { cause: err }));
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...modalMessages.errorNotificationTitle} />,
        content: <FormattedMessage {...modalMessages.errorNotificationContent} />,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    }
  }, [onChangePlan, trackPromoCompleteEvent, trackPromoErrorEvent]);

  const onEmailMarketingModalClose = useCallback(() => {
    trackPromoCancelEvent();
    hideMarketingHandoffModal();
    hideAllModals();
  }, [hideMarketingHandoffModal, trackPromoCancelEvent]);

  if (!promotionalPlanDetails) {
    return null;
  }

  // When self-migration enabled and starting from email handoff, return null after opening drawer
  if (shouldStartWithEmailHandoff && isSelfMigrationEnabled) {
    return null;
  }

  return (
    <>
      {modalToShow === ModalsToShow.EmailHandoff && (
        <MigratePlanEmailMarketingHandoffModal
          billingPlanId={promotionalPlanDetails.billingPlanId}
          onClose={onEmailMarketingModalClose}
          planName={promotionalPlanDetails.name}
          viewOffer={showMigratePlanModal}
        />
      )}

      {modalToShow === ModalsToShow.MigratePlan && (
        <MigratePlanModal
          currentPlanDetails={currentPlanDetails}
          isActionDisabled={isRequestingPlanChange}
          onAction={handleOfferAcceptance}
          onClose={onClose}
          promotionalPlanDetails={promotionalPlanDetails}
        />
      )}
      {modalToShow === ModalsToShow.SuccessfullyMigrated && (
        <MigrationPlanGetRewardedModal onClose={onClose} promotionalPlanDetails={promotionalPlanDetails} />
      )}
    </>
  );
};
